import { cn } from '@foundation/utils/cn';
import { ReactNode } from 'react';

import {
  HeadlessDrawer,
  HeadlessDrawerPosition,
  HeadlessDrawerRootProps,
  HeadlessDrawerStyles,
} from '@design-system/headless-ui';

import { CloseButton } from './CloseButton';

export type DrawerProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly position?: HeadlessDrawerPosition;
  readonly title?: ReactNode;
  readonly description?: ReactNode;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly closePrevented?: boolean;
  readonly showCloseButton?: boolean;
  readonly initialFocus?: HeadlessDrawerRootProps['initialFocus'];
  readonly finalFocus?: HeadlessDrawerRootProps['finalFocus'];
  readonly 'data-testid'?: string;
};

function getDrawerStyles(
  position: HeadlessDrawerPosition,
): HeadlessDrawerStyles {
  return {
    overlay: 'bg-pmui-bg-inverse/(--opacity-48) backdrop-blur-(--blur-400)',
    panel: cn(
      'bg-pmui-bg-primary text-pmui-fg-primary w-full md:max-w-md h-[95vh] md:max-h-[95vh]',
      'rounded-t-md md:rounded-md',
      position === 'right' ? 'md:mr-5' : 'md:ml-5',
    ),
    swipeHandle:
      'mt-2 h-1 w-12 rounded-full bg-pmui-interactive-bg-default-pressed',
    closeButtonContainer: 'p-4',
    header:
      'text-pmui-fg-primary px-5 py-3 min-h-16 pr-16 border-b border-pmui-border-primary',
    buttonGroup:
      'sticky bottom-0 px-5 py-4 bg-pmui-bg-primary border-t border-pmui-border-primary',
    title: 'text-xl leading-8 tracking-tight font-semibold',
  };
}

export function Drawer({
  isOpen,
  onClose,
  position = 'right',
  title,
  description,
  children,
  footer,
  closePrevented = false,
  showCloseButton = true,
  initialFocus,
  finalFocus,
  'data-testid': dataTestId,
}: DrawerProps) {
  return (
    <HeadlessDrawer.Root
      isOpen={isOpen}
      onClose={onClose}
      position={position}
      closePrevented={closePrevented}
      showCloseButton={showCloseButton}
      initialFocus={initialFocus}
      finalFocus={finalFocus}
      closeButton={
        <CloseButton
          size="medium"
          variant="circle"
        />
      }
      styles={getDrawerStyles(position)}
      data-testid={dataTestId}
    >
      {(title || description) && (
        <HeadlessDrawer.Header>
          <div className="min-w-0">
            {title &&
              (typeof title === 'string' ? (
                <HeadlessDrawer.Title>{title}</HeadlessDrawer.Title>
              ) : (
                title
              ))}
            {description &&
              (typeof description === 'string' ? (
                <HeadlessDrawer.Description>
                  {description}
                </HeadlessDrawer.Description>
              ) : (
                description
              ))}
          </div>
        </HeadlessDrawer.Header>
      )}

      <HeadlessDrawer.Body>{children}</HeadlessDrawer.Body>

      {footer && (
        <HeadlessDrawer.ButtonGroup>{footer}</HeadlessDrawer.ButtonGroup>
      )}
    </HeadlessDrawer.Root>
  );
}

export const DrawerClose = HeadlessDrawer.Close;
